import type { FC } from 'react';
import { ArrowUpRight, ShieldCheck } from 'lucide-react';
import { defineMessages, useIntl } from 'react-intl';

import type { A2uiNodeViewProps } from '@/app/lib/a2ui/catalog.tsx';
import { useSurfaceAction } from '@/app/lib/a2ui/surface-context.ts';
import { arr, optStr, str } from '@/app/lib/a2ui/props.ts';

const SHIVI_PORTRAIT =
  'https://storage.agentplace.io/U6HbkGgcRoM8Z3lpbOErwz8zS3uyzV5NPltxXyS1-9c/oxp7mlroebia/media/1789113327915-87c1c8ea-bb21-4474-b059-79fb8ddb62d8-shivi-avatar.png';

const messages = defineMessages({
  tagline: {
    id: 'sourcingHome.tagline',
    defaultMessage: 'Discover. Verify. Trade.',
    description: 'Shivurt brand tagline shown above the home headline.',
  },
  portraitAlt: {
    id: 'sourcingHome.portraitAlt',
    defaultMessage: 'Shivi, the Shivurt procurement guide, wearing a headset and waving',
    description: 'Alternative text for the illustrated portrait of the agent.',
  },
  waysToStart: {
    id: 'sourcingHome.waysToStart',
    defaultMessage: 'Where would you like to start?',
    description: 'Heading above the clickable ways to begin a sourcing conversation.',
  },
  examplesHeading: {
    id: 'sourcingHome.examplesHeading',
    defaultMessage: 'Or start from an example',
    description: 'Heading above example sourcing requests the buyer can click.',
  },
});

interface Capability {
  id: string;
  title: string;
  description: string;
  prompt: string;
}

function readCapabilities(value: unknown): Capability[] {
  return arr(value)
    .filter(
      (entry): entry is Record<string, unknown> =>
        typeof entry === 'object' && entry !== null && !Array.isArray(entry),
    )
    .map((entry) => ({
      id: str(entry.id),
      title: str(entry.title),
      description: str(entry.description),
      prompt: str(entry.prompt),
    }))
    .filter((entry) => entry.title.length > 0);
}

export const SourcingHome: FC<A2uiNodeViewProps> = ({ node }) => {
  const intl = useIntl();
  const dispatch = useSurfaceAction();
  const headline = str(node.props.headline);
  const subline = str(node.props.subline);
  const capabilities = readCapabilities(node.props.capabilities);
  const examples = arr(node.props.exampleRequests).map(str).filter(Boolean);
  const note = optStr(node.props.note);

  return (
    <div className="flex flex-col gap-10">
      <section className="overflow-hidden rounded-xl bg-brand-ink text-brand-ink-foreground shadow-elevated">
        <div className="flex flex-col gap-6 p-6 sm:flex-row sm:items-center sm:gap-8 sm:p-8">
          <div className="min-w-0 flex-1">
            <p
              className="text-body-xs font-semibold uppercase tracking-caps text-brand-accent"
              translate="no"
            >
              {intl.formatMessage(messages.tagline)}
            </p>
            <h1 className="mt-3 text-balance font-display text-heading-xl font-bold tracking-tight text-brand-ink-foreground">
              {headline}
            </h1>
            {subline ? (
              <p className="mt-3 max-w-[60ch] text-body-base text-brand-ink-foreground/80">
                {subline}
              </p>
            ) : null}
          </div>
          <img
            src={SHIVI_PORTRAIT}
            alt={intl.formatMessage(messages.portraitAlt)}
            width={220}
            height={220}
            className="mx-auto h-36 w-36 shrink-0 rounded-full bg-brand-ink-foreground object-cover object-top sm:h-44 sm:w-44"
          />
        </div>
      </section>

      {capabilities.length > 0 ? (
        <section>
          <h2 className="text-body-sm font-semibold uppercase tracking-caps text-muted-foreground">
            {intl.formatMessage(messages.waysToStart)}
          </h2>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            {capabilities.map((capability, index) => (
              <button
                key={capability.id || capability.title}
                type="button"
                onClick={() =>
                  dispatch?.('chooseCapability', {
                    id: capability.id,
                    prompt: capability.prompt || capability.title,
                  })
                }
                style={{ animationDelay: `${index * 70}ms` }}
                className="group animate-fadeUp touch-manipulation rounded-lg border border-border bg-card p-5 text-left opacity-0 transition-shadow duration-150 ease-out hover:border-brand-accent hover:shadow-lift focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-primary motion-reduce:animate-none motion-reduce:opacity-100"
              >
                <div className="flex items-start justify-between gap-3">
                  <span className="min-w-0 text-body-lg font-semibold tracking-tight text-foreground">
                    {capability.title}
                  </span>
                  <ArrowUpRight
                    aria-hidden="true"
                    className="mt-1 h-4 w-4 shrink-0 text-muted-foreground-subtle transition-colors duration-150 group-hover:text-brand-accent"
                  />
                </div>
                <p className="mt-2 text-body-sm text-muted-foreground">{capability.description}</p>
              </button>
            ))}
          </div>
        </section>
      ) : null}

      {examples.length > 0 ? (
        <section>
          <h2 className="text-body-sm font-semibold uppercase tracking-caps text-muted-foreground">
            {intl.formatMessage(messages.examplesHeading)}
          </h2>
          <ul className="mt-4 flex flex-col gap-2">
            {examples.map((example) => (
              <li key={example}>
                <button
                  type="button"
                  onClick={() => dispatch?.('useExample', { request: example })}
                  className="flex w-full touch-manipulation items-center gap-3 rounded-lg border border-border bg-card px-4 py-3 text-left text-body-sm text-foreground transition-colors duration-150 ease-out hover:border-brand-accent hover:bg-muted focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-primary active:scale-[0.99]"
                >
                  <span className="min-w-0 flex-1 break-words">{example}</span>
                  <ArrowUpRight
                    aria-hidden="true"
                    className="h-4 w-4 shrink-0 text-brand-accent"
                  />
                </button>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {note ? (
        <p className="flex items-start gap-2 text-body-sm text-muted-foreground">
          <ShieldCheck aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-brand-accent" />
          <span className="min-w-0">{note}</span>
        </p>
      ) : null}
    </div>
  );
};
